import type { BaseClient } from '../../http/BaseClient.js';
import type {
  BICatalogSummary,
  BIEntityDetail,
  ListBIDatasetsResponse,
  ListBIModelsResponse,
  SelectBIDatasetsRequest,
  SelectBIDatasetsResponse,
  TriggerBIDiscoveryResponse,
} from './types.js';

/**
 * BIClient is the Talk-to-Your-BI control-plane surface used by the data-agent
 * frontend: read the discovered semantic-model catalog, list connected models,
 * trigger a re-scan, and start the Power BI OAuth connect flow. All endpoints
 * are org-scoped + product-entitlement-gated server-side (requireProduct("bi")).
 *
 * Catalog upsert + refresh-token rotation are machine-only (HMAC, engine→backend)
 * and deliberately NOT exposed here.
 */
export class BIClient {
  constructor(
    private readonly http: BaseClient,
    private readonly baseUrl: string,
  ) {}

  /**
   * Compact grounding catalog for a workspace's model: entities + measures +
   * relationship names, no per-column detail. Pass `entity` to get the full
   * (ranked) field list for one entity — the get_model_schema detail layer.
   */
  getCatalog(workspaceId: string, opts?: { modelId?: string; entity?: string }): Promise<BICatalogSummary | BIEntityDetail> {
    const params = new URLSearchParams();
    if (opts?.modelId) params.set('modelId', opts.modelId);
    if (opts?.entity) params.set('entity', opts.entity);
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<BICatalogSummary | BIEntityDetail>(`/workspaces/${workspaceId}/bi/catalog${qs}`);
  }

  /** Lists the connected semantic models for a data project. */
  listModels(workspaceId: string): Promise<ListBIModelsResponse> {
    return this.http.get<ListBIModelsResponse>(`/workspaces/${workspaceId}/bi/models`);
  }

  /**
   * Lists the connected account's workspaces + datasets for the dataset picker.
   * The backend forwards to the engine (which holds the decrypted token) to make
   * the Power BI REST calls. `credentialId` selects which connected account.
   */
  listDatasets(workspaceId: string, opts: { credentialId: string }): Promise<ListBIDatasetsResponse> {
    const qs = `?credentialId=${encodeURIComponent(opts.credentialId)}`;
    return this.http.get<ListBIDatasetsResponse>(`/workspaces/${workspaceId}/bi/datasets${qs}`);
  }

  /**
   * Connects the chosen datasets: creates one bi_model per selection (the
   * workspace GUID is stored on the model). Entities/measures are discovered
   * lazily on the first chat question.
   */
  selectDatasets(workspaceId: string, body: SelectBIDatasetsRequest): Promise<SelectBIDatasetsResponse> {
    return this.http.post<SelectBIDatasetsResponse>(`/workspaces/${workspaceId}/bi/models`, { body });
  }

  /**
   * Triggers an engine-side catalog re-scan of the workspace's connected model
   * (INFO.* discovery → bi_* upsert). Returns once the backend has accepted the
   * request; the catalog read reflects the new data when discovery completes.
   */
  triggerDiscovery(workspaceId: string): Promise<TriggerBIDiscoveryResponse> {
    return this.http.post<TriggerBIDiscoveryResponse>(`/workspaces/${workspaceId}/bi/discover`, {});
  }

  /**
   * The browser URL that starts the delegated Power BI OAuth flow for a data
   * project. Navigate the window here; the backend redirects to Microsoft and,
   * on the callback, creates the POWERBI credential and returns to the frontend.
   * `workspaceId` tells the backend which project to attach the credential to.
   */
  powerBIConnectUrl(workspaceId: string): string {
    return `${this.baseUrl}/connect/powerbi?workspaceId=${encodeURIComponent(workspaceId)}`;
  }
}
